import { AUTO_ENGINE, INSURANCE_AUTO } from "./insuranceAuto";

/**
 * Returns the correct set of warranty features based on vehicle conditions.
 * This replicates the exact logic from the source-of-truth Vue templates:
 * - GarantiesAccordesAuto.vue
 * - GarantiesAccordesMoto.vue
 * - GarantieCampingCarView.vue
 * 
 * @param {Object} options
 * @param {number|string} options.type - The ID of the warranty type
 * @param {number|string} options.engine - The ID of the engine/fuel type (for Auto)
 * @param {number|string} options.age - The age of the vehicle (for dynamic "Important" content)
 * @param {string} options.category - 'AUTO', 'MOTO', or 'CAMPINGCAR'
 * @returns {Array} Array of feature objects { title, content }
 */
export const getWarrantyFeatures = ({ type, engine, age, category = 'AUTO' }) => {
    // Normalize inputs
    const typeId = Number(type);
    const engineId = Number(engine);
    const vehicleAge = Number(age);
    const cat = (category || 'AUTO').toUpperCase();

    let details = null;
    if (cat === 'AUTO') {
        details = WARRANTY_DETAILS[typeId];
    } else if (cat === 'MOTO') {
        details = MOTO_WARRANTY_DETAILS[typeId];
    } else if (cat === 'CAMPING-CAR') {
        details = CAMPINGCAR_WARRANTY_DETAILS[typeId];
    }

    if (!details) return [];

    let featuresList = [];

    // 1. Handle Auto Electric Logic
    if (cat === 'AUTO' && engineId === AUTO_ENGINE.ELECTRIC.ID) {
        // If electric, we only return electricFeatures if they exist for this type
        // In the template, Standard (3) returns nothing for Electric
        featuresList = details.electricFeatures ? [...details.electricFeatures] : [];
    } else {
        // Otherwise return detailedFeatures
        featuresList = details.detailedFeatures ? [...details.detailedFeatures] : [];
    }

    // 2. Handle Dynamic Logic (e.g. Age-based content)
    return featuresList.map(feature => {
        let content = feature.content;

        // Auto Réseau (Non-Electric) has age-based "Important" content
        if (cat === 'AUTO' && typeId === INSURANCE_AUTO.TYPE_RESEAU.ID &&
            feature.title === "Important" && engineId !== AUTO_ENGINE.ELECTRIC.ID) {
            content = vehicleAge < 10
                ? "Cette garantie est réservée aux véhicules de moins de 10 ans, avec une cylindrée, au maximum de 3.5 litres et sans la limite kilométrique à la souscription."
                : "Cette garantie est réservée aux véhicules de moins de 15 ans, avec une cylindrée, au maximum de 4.5 litres et n'excédant pas la limite kilométrique à la souscription de 150 000 km.";
        }

        return {
            ...feature,
            content
        };
    });
};

export const WARRANTY_DETAILS = {
    // STANDARD (ID 3)
    [INSURANCE_AUTO.TYPE_STANDARD.ID]: {
        detailedFeatures: [
            {
                title: "Moteur",
                content: "L'arbre à cames, les coussinets de tête de bielle, les bielles, les culbuteurs et tiges de culbuteurs, les poussoirs, chaînes de distribution, courroie de distribution (uniquement en cas de rupture), paliers de vilebrequin, vilebrequin, pistons et axes, segments, soupapes et guides, pompe à huile, culasse et joint de culasse."
            },
            {
                title: "Boîte de vitesse",
                content: `<i>Manuelle : </i>Toutes les défaillances internes : Pignon, arbres, sélecteurs, synchros, roulements.<br><i>Automatique : </i>Pignons, pompe à huile, bagues et arbres, convertisseur de couple, vannes et soupapes, régulateur, plateau.`
            },
            {
                title: "Dispositif de transmission",
                content: "Boite de transfert, pont arrière, différentiels, pignons et couronnes. A l’exception des arbres de roues, de transmission et de cardans."
            },
            {
                title: "Important",
                content: "Cette garantie est réservée aux véhicules de moins de 15 ans, avec une cylindrée, au maximum de 3.5 litres et n'excédant pas la limite kilométrique à la souscription de 250 000 km."
            }
        ]
    },

    // RÉSEAU (ID 4)
    [INSURANCE_AUTO.TYPE_RESEAU.ID]: {
        detailedFeatures: [
            {
                title: "Moteur",
                content: "L'arbre à cames, les coussinets de tête de bielle, les bielles, les culbuteurs et tiges de culbuteurs, les poussoirs, chaînes de distribution, courroie de distribution (uniquement en cas de rupture), paliers de vilebrequin, vilebrequin, pistons et axes, segments, soupapes et guides, pompe à huile, culasse et joint de culasse, turbo."
            },
            {
                title: "Boîte de vitesse",
                content: "Manuelle : Toutes les défaillances internes : Pignon, arbres, sélecteurs, synchros, roulements.<br>Automatique : Pignons, pompe à huile, bagues et arbres, convertisseur de couple, vannes et soupapes, régulateur, plateau."
            },
            {
                title: "Dispositif de transmission",
                content: "Boîte de transfert, pont arrière, différentiels, pignons et couronnes, cardans, arbre de transmission, arbres de roues."
            },
            {
                title: "Composants complémentaires pour les véhicules hybrides",
                content: "Moteur électrique, convertisseur, chargeur, calculateur, onduleur, motoventilateur."
            },
            {
                title: "Système de refroidissement",
                content: "Radiateur d'eau, pompe à eau, ventilateur."
            },
            {
                title: "Système de freinage",
                content: "Maître-cylindre, servofrein."
            },
            {
                title: "Système d'alimentation",
                content: "Pompe à injection, pompes d'alimentation en carburant, boitier papillon d'injection, à l'exception des injecteurs."
            },
            {
                title: "Dispositif d'embrayage",
                content: "Mécanisme, fourchette."
            },
            {
                title: "Important",
                content: "" // Dynamic in getWarrantyFeatures
            }
        ],
        electricFeatures: [
            {
                title: "Dans le système Flux électrique",
                content: "Moteur électrique, convertisseur, chargeur, calculateur, onduleur, motoventilateur, batterie de traction."
            },
            {
                title: "Dans le dispositif de Transmission",
                content: "Arbre de transmission, différentiels, cardans."
            },
            {
                title: "Important",
                content: "Cette garantie est réservée aux véhicules électriques de moins de 10 ans, avec une puissance, au maximum de 400 Kw et n'excédant pas la limite kilométrique à la souscription de 150 000 km."
            }
        ]
    },

    // MAXI RÉSEAU (ID 5)
    [INSURANCE_AUTO.TYPE_MAXI_RESEAU.ID]: {
        detailedFeatures: [
            {
                title: "Moteur",
                content: "L'arbre à cames, les coussinets de tête de bielle, les bielles, les culbuteurs et tiges de culbuteurs, les poussoirs, chaînes de distribution, courroie de distribution (uniquement en cas de rupture), les paliers de vilebrequin, le vilbrequin, les pistons et axes, les segments, les soupapes et guides, pompe à huile, la culasse, et joint de culasse, turbo."
            },
            {
                title: "Boîte de vitesse",
                content: `<i>Manuelle : </i>Toutes les défaillances internes : Pignon, arbres, sélecteurs, synchros, roulements.<br><i>Automatique : </i>Pignons, pompe à huile, bagues et arbres, convertisseur de couple, vannes et soupapes, régulateur, plateau.`
            },
            {
                title: "Dispositif de transmission",
                content: "Boite de transfert, pont arrière, différentiels, pignons et couronnes, cardans, arbre de transmission, arbres de roues."
            },
            {
                title: "Composants complémentaires pour les véhicules hybrides",
                content: "Moteur électrique, convertisseur, chargeur, calculateur, onduleur, motoventilateur."
            },
            {
                title: "Système de refroidissement",
                content: "Radiateur d'eau, pompe à eau, ventilateur."
            },
            {
                title: "Système électrique",
                content: "Alternateur, démarreur, serrures de portes, moteur et mécanisme de lève-vitres, moteur d'essuie-glace et de toit ouvrant, compresseur et condenseur de climatisation, à l'exception de la recharge de gaz."
            },
            {
                title: "Système de direction",
                content: "Crémaillère ou boitier, pompe d'assistance. A l'exception de la colonne, des rotules et des biellettes."
            },
            {
                title: "Système de freinage",
                content: "Maître-cylindre, servofrein, répartiteur, pompe à vide, bloc hydraulique ABS."
            },
            {
                title: "Système d'alimentation",
                content: "Allumeur, module électronique d'allumage, bobine, pompe à injection, pompes d'alimentation en carburant, boitier papillon d'injection, à l'exception des injecteurs."
            },
            {
                title: "Dispositif d'embrayage",
                content: "Mécanisme, fourchette, récepteur, émetteur."
            },
            {
                title: "Important",
                content: "Cette garantie est réservée aux véhicules de moins de 7 ans, avec une cylindrée, au maximum de 3.5 litres et n'excédant pas la limite kilométrique à la souscription de 120 000 km pour les véhicules essence et 150 000 km pour les véhicules diesel."
            }
        ],
        electricFeatures: [
            {
                title: "Dans le système Flux électrique",
                content: "Moteur électrique, convertisseur, chargeur, calculateur, onduleur, motoventilateur, batterie de traction."
            },
            {
                title: "Dans le dispositif de Transmission",
                content: "Arbre de transmission, différentiels, cardans."
            },
            {
                title: "Dans le système de Refroidissement",
                content: "Radiateur d’eau, pompe à eau, ventilateur."
            },
            {
                title: "Dans le système de Freinage",
                content: "Maître-cylindre, servofrein, bloc ABS."
            },
            {
                title: "Dans le système de Direction",
                content: "Crémaillère ou boîtier, pompe d’assistance. A l’exception de la colonne, des rotules et des biellettes."
            },
            {
                title: "Dans le système Électrique",
                content: "Serrure de porte, moteurs et mécanismes de lèves vitres, moteurs d’essuie-glace et de toit ouvrant, compresseur et condenseur de climatisation, à l’exception de la recharge de gaz. Potentiomètre pédale accélérateur."
            },
            {
                title: "Important",
                content: "Cette garantie est réservée aux véhicules électriques de moins de 7 ans, avec une puissance au maximum de 400 Kw et n'excédant pas la limite kilométrique à la souscription de 100 000 km."
            }
        ]
    },

    // MAXI PLUS (ID 1)
    [INSURANCE_AUTO.TYPE_MAXI_PLUS.ID]: {
        detailedFeatures: [
            {
                title: "Moteur",
                content: "L'arbre à cames, les coussinets de tête de bielle, les bielles, les culbuteurs et tiges de culbuteurs, poussoirs, chaînes de distribution, courroie de distribution (uniquement en cas de rupture), paliers de vilebrequin, vilebrequin, pistons et axes, segments, soupapes et guides, pompe à huile, culasse et joint de culasse, turbo."
            },
            {
                title: "Boîte de vitesse",
                content: `<i>Manuelle : </i>Toutes les défaillances internes : Pignons, les arbres sélecteurs synchros, les roulements.<br><i>Automatique : </i>Pignons, pompe à huile, les bagues et les arbres, convertisseur de couple, vannes et soupapes, régulateur, plateau.`
            },
            {
                title: "Dispositif de transmission",
                content: "Boîte de transfert, pont arrière, différentiels, pignons et couronnes, cardans, arbre de transmission, arbres de roues."
            },
            {
                title: "Composants complémentaires pour les véhicules hybrides",
                content: "Moteur électrique, convertisseur, chargeur, calculateur, onduleur, motoventilateur."
            },
            {
                title: "Système de refroidissement",
                content: "Radiateur d'eau, pompe à eau, ventilateur, échangeur air, thermostat, refroidisseur d'huile moteur et BV."
            },
            {
                title: "Système de direction",
                content: "Crémaillère ou boitier, pompe d'assistance, canalisation, réservoir. A l'exception de la colonne, des rotules et des biellettes."
            },
            {
                title: "Système électrique",
                content: "Alternateur, démarreur, serrures de portes, moteur et mécanisme de lève-vitres, moteur d'essuie-glace et de toit ouvrant, compresseur et condenseur de climatisation, à l'exception de la recharge de gaz."
            },
            {
                title: "Système de freinage",
                content: "Maître-cylindre, servofrein, répartiteur, limiteur, pompe à vide, bloc hydraulique ABS, calculateur ABS, cylindres de roues, étriers de frein."
            },
            {
                title: "Système d'alimentation",
                content: "Allumeur, module électronique d'allumage, bobine, pompe à injection, boitier électronique d'injection, pompes d'alimentation en carburant, boitier papillon d'injection, injecteurs, vanne EGR, débitmètre."
            },
            {
                title: "Dispositif d'embrayage",
                content: "Mécanisme, fourchette, récepteur, émetteur, volant-moteur."
            },
            {
                title: "Dispositif de suspension",
                content: "Ressorts, barres stabilisatrices, bras de suspension, rotules de suspension."
            },
            {
                title: "Important",
                content: "Cette garantie est réservée aux véhicules de moins de 5 ans, avec une cylindrée, au maximum de 3.5 litres and n'excédant pas la limite kilométrique à la souscription de 150 000 km."
            }
        ]
    },

    // PRESTIGE (ID 2)
    [INSURANCE_AUTO.TYPE_PRESTIGE.ID]: {
        detailedFeatures: [
            {
                title: "Moteur",
                content: "L'arbre à cames, les coussinets de tête de bielle, les bielles, les culbuteurs et tiges de culbuteurs, poussoirs, chaïnes de distribution, courroie de distribution (uniquement en cas de rupture), paliers de vilebrequin, vilebrequin, pistons et axes, segments, soupapes et guides, pompe à huile, culasse et joint de culasse, turbo."
            },
            {
                title: "Boîte de vitesse",
                content: `<i>Manuelle : </i>Toutes les défaillances internes : Pignon, arbres, sélecteurs, synchros, roulements.<br><i>Automatique : </i>Pignons, pompe à huile, bagues and arbres, convertisseur de couple, vannes and soupapes, régulateur, plateau, calculateur BVA.`
            },
            {
                title: "Dispositif de transmission",
                content: "Boîte de transfert, pont arrière, différentiels, pignons et couronnes, cardans, arbre de transmission, arbres de roues."
            },
            {
                title: "Composants complémentaires pour les véhicules hybrides",
                content: "Moteur électrique, convertisseur, chargeur, calculateur, onduleur, motoventilateur."
            },
            {
                title: "Système de refroidissement",
                content: "Radiateur d'eau, pompe à eau, ventilateur, échangeur air, thermostat, refroidisseur d'huile moteur et BV."
            },
            {
                title: "Système de direction",
                content: "Crémaillère ou boîtier, pompe d'assistance, canalisation, réservoir. A l'exception de la colonne, des rotules et des biellettes."
            },
            {
                title: "Système électrique",
                content: "Alternateur, démarreur, serrures de portes, moteur et mécanisme de lève-vitres, moteur d'essuie-glace et de toit ouvrant, commodo/com 2000, compresseur et condenseur de climatisation, à l'exception de la recharge de gaz."
            },
            {
                title: "Système de freinage",
                content: "Maître-cylindre, servofrein, répartiteur, limiteur, pompe à vide, bloc hydraulique ABS, calculateur ABS, cylindres de roues, étriers de frein."
            },
            {
                title: "Système d'alimentation",
                content: "Allumeur, module électronique d'allumage, bobine, pompe à injection, boîtier électronique d'injection, pompes d'alimentation en carburant, boîtier papillon d'injection, injecteurs, vanne EGR, débitmètre."
            },
            {
                title: "Système d'embrayage",
                content: "Mécanisme, fourchette, récepteur, émetteur, volant-moteur."
            },
            {
                title: "Dispositif de suspension",
                content: "Ressorts, barres stabilisatrices, bras de suspension, rotules de suspension."
            },
            {
                title: "Important",
                content: "Cette garantie est réservée aux véhicules de moins de 5 ans, avec une cylindrée, au maximum de 3.5 litres and n'excédant pas la limite kilométrique à la souscription de 150 000 km."
            }
        ]
    }
};

export const MOTO_WARRANTY_DETAILS = {
    // STANDARD (ID 1)
    1: {
        detailedFeatures: [
            {
                title: "Moteur",
                content: "Pièces lubrifiées mobiles et internes (attelage mobile, cylindres ou chemises, chaînes et pignons de distribution, pompe à huile, roulement de bielle, culasses et joints de culasse) ainsi que tous les dommages mécaniques portant sur le bloc moteur et résultant directement de l’avarie. Le remplacement de la chaîne de distribution et les avaries résultant de sa dégradation ne seront pris en charge que si son taux d’usure n’est pas supérieur à celui toléré par le constructeur."
            },
            {
                title: "Boîte de vitesse",
                content: `Toutes les pièces internes du bloc, soit :<br><i>- pour la boite mécanique : </i>pièces lubrifiées mobiles et internes de la boîte (pignons, arbres, paliers, roulements, synchros, circlips, fourchettes de commande) ainsi que tous les dommages mécaniques portant sur le carter et qui résultent directement de l’avarie.<br><i>- pour la Transmission automatique : </i>Variateurs et réducteurs (joints et courroies de transmission exclus).`
            }
        ]
    },
    // COMPLÈTE (ID 2)
    2: {
        detailedFeatures: [
            {
                title: "Moteur",
                content: "Pièces lubrifiées mobiles et internes (attelage mobile, cylindres ou chemises, chaînes et pignons de distribution, pompe à huile, roulement de bielle, culasses et joints de culasse) ainsi que tous les dommages mécaniques portant sur le bloc moteur et résultant directement de l’avarie. Le remplacement de la chaîne de distribution et les avaries résultant de sa dégradation ne seront pris en charge que si son taux d’usure n’est pas supérieur à celui toléré par le constructeur."
            },
            {
                title: "Boîte de vitesse",
                content: `Toutes les pièces internes du bloc, soit :<br>- pour la boite mécanique : pièces lubrifiées mobiles et internes de la boîte (pignons, arbres, paliers, roulements, synchros, circlips, fourchettes de commande) ainsi que tous les dommages mécaniques portant sur le carter et qui résultent directement de l’avarie.<br>- pour la Transmission automatique : Variateurs et réducteurs (joints et courroies de transmission exclus)`
            },
            {
                title: "Transmission secondaire à cardan",
                content: "Couple conique (sauf joints spy), roulements, arbre de transmission (sauf soufflet de cardan)."
            },
            {
                title: "Embrayage",
                content: "Emetteur, récepteur hydraulique."
            },
            {
                title: "Circuit d’alimentation de série",
                content: "Carburateur, pompes d’alimentation, injecteurs."
            },
            {
                title: "Circuit électrique et électronique de série",
                content: "Alternateur, démarreur et modules électroniques de gestion d’allumage et d’alimentation moteur."
            },
            {
                title: "Circuit de refroidissement",
                content: "Pompe à eau, radiateurs (eau et/ou huile), moto-ventilateurs, calorstat."
            },
            {
                title: "Freins",
                content: "Maître-cylindre, calculateur et capteur antiblocage des roues, émetteur, étriers, servofrein."
            },
            {
                title: "Suspension",
                content: "Axes de pivots et bagues, ressorts, rotules, roulements de roues, roulements de bras oscillant et de fourchettes. Les amortisseurs sont exclus."
            }
        ]
    }
};

export const CAMPINGCAR_WARRANTY_DETAILS = {
    // RÉSEAU (ID 1)
    1: {
        detailedFeatures: [
            {
                title: "Moteur",
                content: "Arbre à cames, coussinets de bielles, bielles, culbuteurs, tiges de culbuteurs, poussoirs, chaînes de distribution, courroie de distribution (uniquement en cas de rupture), paliers de vilebrequin, vilebrequin, pistons et axes, segments, soupapes et guides, pompe à huile, culasse et joint de culasse, turbo."
            },
            {
                title: "Dans la Boite de vitesses",
                content: "<b>Manuelle :</b> Toutes les défaillances internes : pignons, arbres, sélecteurs, synchros, roulements.<br><b>Automatique :</b> pignons, pompe à huile, bagues et arbres, convertisseur de couple, vannes et soupapes, régulateur, plateau."
            },
            {
                title: "Dans le dispositif de transmission",
                content: "Boîte de transfert, pont arrière, différentiels, pignons et couronnes. À l’exclusion des arbres de roues, de transmission et des cardans."
            },
            {
                title: "Pour la cellule",
                content: "Pompe à eau, carte électronique chauffe-eau, agrégat réfrigérateur, transformateur et chargeur électrique, pulseur d’air, carte électronique du réfrigérateur, à l’exception des faisceaux. Les problèmes d’étanchéité sont couverts sous réserve du respect des contrôles annuels constructeur."
            },
            {
                title: "Important",
                content: "Cette garantie est réservée aux camping-cars de moins de 5 tonnes et moins de 120 000 kilomètres à la souscription."
            }
        ]
    },

    // MAXI RÉSEAU (ID 2)
    2: {
        detailedFeatures: [
            {
                title: "Moteur",
                content: "Arbre à cames, coussinets de bielles, bielles, culbuteurs, tiges de culbuteurs, poussoirs, chaines de distribution, courroie de distribution (uniquement en cas de rupture), paliers de vilebrequin, vilebrequin, pistons et axes, segments, soupapes et guides, pompe à huile, culasse et joint de culasse et turbo."
            },
            {
                title: "Dans la Boite de vitesses",
                content: "<b>Manuelle :</b> Toutes les défaillances internes : pignons, arbres, sélecteurs, synchros, roulements.<br><b>Automatique :</b> pignons, pompe à huile, bagues et arbres, convertisseur de couple, vannes et soupapes, régulateur, plateau."
            },
            {
                title: "Dans le dispositif de transmission",
                content: "Boîte de transfert, pont arrière, différentiels, pignons et couronnes. À l’exclusion des arbres de roues, de transmission et des cardans."
            },
            {
                title: "Dans le dispositif de refroidissement",
                content: "Radiateur d'eau, pompe à eau, ventilateur."
            },
            {
                title: "Dans le système électrique",
                content: "Alternateur, démarreur, serrure de porte, moteurs et mécanismes de lève-vitres, moteurs d’essuie-glace et de toit ouvrant, compresseur et condenseur de climatisation (hors recharge de gaz)."
            },
            {
                title: "Dans le système de direction",
                content: "Crémaillère ou boîtier, pompe d’assistance, à l’exception de la colonne, des rotules et biellettes."
            },
            {
                title: "Dans le système de freinage",
                content: "Maître-cylindre, servofrein, répartiteur, pompe à vide, bloc hydraulique ABS."
            },
            {
                title: "Dans le système d’alimentation",
                content: "Allumeur, module électronique d’allumage, bobine, pompe à injection, boîtier électronique d’injection, pompes d’alimentation en carburant, boîtier papillon, débitmètre, vanne EGR. À l’exception des injecteurs."
            },
            {
                title: "Dans le dispositif d’embrayage",
                content: "Mécanisme, fourchette, émetteur, récepteur."
            },
            {
                title: "Pour la cellule",
                content: "Pompe à eau, carte électronique chauffe-eau, agrégat réfrigérateur, transformateur et chargeur électrique, pulseur d’air, carte électronique du réfrigérateur, à l’exception des faisceaux. Les problèmes d’étanchéité sont couverts sous réserve du respect des contrôles annuels constructeur."
            },
            {
                title: "Important",
                content: "Cette garantie est réservée aux camping-cars de moins de 5 tonnes et moins de 120 000 kilomètres à la souscription."
            }
        ]
    },
    // MAXI PLUS (ID 3)
    3: {
        detailedFeatures: [
            {
                title: "Moteur",
                content: "Arbre à cames, coussinets de bielles, bielles, culbuteurs, tiges de culbuteurs, poussoirs, chaines de distribution, courroie de distribution (uniquement en cas de rupture), paliers de vilebrequin, vilebrequin, pistons et axes, segments, soupapes et guides, pompe à huile, culasse et joint de culasse et turbo."
            },
            {
                title: "Dans la Boite de vitesses",
                content: "<b>Manuelle :</b> Toutes les défaillances internes : Pignons, arbres, sélecteurs, synchros, roulements.<br><b>Automatique :</b> Pignons, pompe à huile, bagues et arbres, convertisseur de couple, vannes et soupapes, régulateur, plateau."
            },
            {
                title: "Dans le dispositif de transmission",
                content: "Boîte de transfert, pont arrière, différentiels, pignons et couronnes. A l’exclusion des arbres de roues, de transmission et des cardans."
            },
            {
                title: "Dans le dispositif de refroidissement",
                content: "Radiateur d'eau, pompe à eau, ventilateur."
            },
            {
                title: "Dans le système électrique",
                content: "Alternateur, démarreur, serrure de porte, moteurs et mécanismes de lèves vitres, moteurs d'essuie glace et de toit ouvrant, compresseur et condenseur de climatisation à l’exception de la recharge de gaz."
            },
            {
                title: "Dans le système de direction",
                content: "Crémaillère ou boîtier, pompe d'assistance, à l'exception de la colonne, des rotules et biellettes."
            },
            {
                title: "Dans le système de freinage",
                content: "Maître cylindre, servofrein, répartiteur, pompe à vide, bloc hydraulique ABS."
            },
            {
                title: "Dans le système d’alimentation",
                content: "Allumeur, module électronique d'allumage, bobine, pompe à injection, boîtier électronique d'injection, pompes d'alimentation en carburant, boitier papillon d’injection, débitmètre, vanne EGR et les injecteurs."
            },
            {
                title: "Dans le dispositif d’embrayage",
                content: "Mécanisme, fourchette, émetteur, récepteur et volant moteur."
            },
            {
                title: "Les pièces de la cellule",
                content: "Pompe à eau, carte électronique chauffe-eau, agrégat réfrigérateur, transformateur et chargeur électrique, pulseur d’air, carte électronique du réfrigérateur, à l’exception des faisceaux, moteur de lit électrique, mécanisme électrique du marchepied, mécanisme de volet-store, moteur d’antenne électrique. Les problèmes d’étanchéité sont couverts sous réserve du respect des contrôles annuels prévus par le constructeur."
            },
            {
                title: "Important",
                content: "Cette garantie est réservée aux camping-cars de moins de 5 tonnes, de moins de 10 ans et moins de 120 000 kilomètres à la souscription."
            }
        ]
    }
};



// WEBPACK FOOTER //
// ./src/constants/warrantyDetails.js